import { useOutsideClick } from '@statsbomb/kitbag-components';
import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { selectedFilterSetIdAtom } from '@/atoms/filters/userFilters';
import { searchValueAtom } from '@/atoms/search/search';

// Shared by the top-bar and Home searches so both keep the same dropdown and selection behaviour.
export const useSearchDropdown = () => {
  const [searchValue, setSearchValue] = useAtom(searchValueAtom);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const wrapperRef = useOutsideClick<HTMLDivElement>(() => setIsResultsOpen(false));
  const setSelectedFilterSetId = useSetAtom(selectedFilterSetIdAtom);

  // Safe while only one search is mounted at a time; Home has no top-bar search
  useEffect(() => () => setSearchValue(''), [setSearchValue]);

  const closeResults = () => setIsResultsOpen(false);

  return {
    searchValue,
    isResultsOpen,
    wrapperRef,
    closeResults,
    // Escape closes the results with focus left where it is, so the user can keep typing.
    handleKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape') closeResults();
    },
    // Tabbing out of the field and its results closes them. A null relatedTarget is a click on a
    // non-focusable area, which the outside-click handler already covers.
    handleBlur: (e: React.FocusEvent<HTMLElement>) => {
      if (e.relatedTarget instanceof Node && !e.currentTarget.contains(e.relatedTarget)) closeResults();
    },
    openResults: () => setIsResultsOpen(true),
    clearSearch: () => {
      setSearchValue('');
      setIsResultsOpen(false);
    },
    changeSearch: (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsResultsOpen(true);
      setSearchValue(e.target.value);
    },
    selectResult: () => {
      setIsResultsOpen(false);
      setSearchValue('');
      setSelectedFilterSetId(null);
    },
  };
};
